/**
 * Noara Share & Product Card Interactions
 * Handles share buttons (native Web Share API → clipboard fallback)
 * and add-to-cart hover bar across all product cards.
 * @module noara-share
 */
(function () {
  'use strict';

  /**
   * Copy text to clipboard using the Clipboard API with textarea fallback.
   * @param {string} text - Text to copy.
   * @returns {Promise<boolean>} Whether the copy succeeded.
   */
  async function copyToClipboard(text) {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      try {
        await navigator.clipboard.writeText(text);
        return true;
      } catch {
        /* fall through to textarea fallback */
      }
    }
    /* Textarea fallback */
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.setAttribute('readonly', '');
    ta.style.cssText = 'position:fixed;left:-9999px;top:-9999px;opacity:0;';
    document.body.appendChild(ta);
    ta.select();
    let ok = false;
    try {
      ok = document.execCommand('copy');
    } catch {
      /* silent */
    }
    document.body.removeChild(ta);
    return ok;
  }

  /**
   * Briefly replace button content with a feedback symbol.
   * @param {HTMLElement} btn - The button element.
   * @param {string} symbol - Symbol to display.
   */
  function showFeedback(btn, symbol) {
    const orig = btn.innerHTML;
    const span = document.createElement('span');
    span.style.cssText = 'font-size:14px;line-height:1;';
    span.textContent = symbol;
    btn.textContent = '';
    btn.appendChild(span);
    setTimeout(() => {
      btn.innerHTML = orig;
    }, 1800);
  }

  /* ── Share buttons (delegated) ─────────────────────────────────── */
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-share]');
    if (!btn) return;
    e.stopPropagation();

    const card  = btn.closest('[data-product-url]');
    const url   = card ? card.dataset.productUrl : window.location.href;
    const titleEl = card ? card.querySelector('.pc__product-title') : null;
    const title = titleEl ? titleEl.textContent.trim() : '';

    if (navigator.share) {
      try {
        await navigator.share({ url, title });
      } catch {
        /* user cancelled or error – silent */
      }
    } else {
      const ok = await copyToClipboard(url);
      if (ok) showFeedback(btn, '\u2713');
    }
  });

  /* ── Add-to-cart hover bar (delegated) ─────────────────────────── */
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-add-to-cart]');
    if (!btn) return;

    const variantId = btn.dataset.variantId;
    if (!variantId) return;

    const origText = btn.textContent;
    btn.textContent = '...';
    btn.disabled = true;

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ items: [{ id: parseInt(variantId, 10), quantity: 1 }] })
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.description || response.statusText);
      }

      btn.textContent = '\u2713 ' + (document.documentElement.lang.startsWith('pt') ? 'ADICIONADO' : 'ADDED');

      setTimeout(() => {
        btn.textContent = origText;
        btn.disabled = false;
      }, 2000);

      /* Open the cart drawer */
      document.dispatchEvent(new CustomEvent('cart:open', {
        detail: { trigger: btn }
      }));
    } catch (err) {
      btn.textContent = origText;
      btn.disabled = false;
    }
  });
})();
